import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PhotoReorder from "@/components/PhotoReorder";
import { api, errText } from "@/lib/api";

export const PhotoManagerDialog = ({ property, open, onOpenChange, onSaved }) => {
  const [images, setImages] = useState(property.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setImages(property.images || []);
  }, [open, property.images]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload?watermark=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.file_id]);
      } catch (err) {
        toast.error(errText(err));
      }
    }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/properties/${property.property_id}/images`, { images });
      toast.success("Photo order saved");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="photo-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Manage photos</DialogTitle>
          <DialogDescription className="text-[13px]">
            Drag photos to reorder them — the first photo is the cover buyers see.
          </DialogDescription>
        </DialogHeader>

        {images.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This listing has no uploaded photos yet — add some below.
          </p>
        ) : (
          <PhotoReorder
            images={images}
            onChange={setImages}
            onRemove={(id) => setImages(images.filter((x) => x !== id))}
            testidPrefix="manage-photo"
          />
        )}

        <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-input p-5 hover:border-foreground transition-colors duration-200">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Upload className="h-5 w-5 text-primary" />}
          <span className="text-[13px] text-muted-foreground">{uploading ? "Uploading…" : "Add more photos"}</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="manage-photo-upload-input" />
        </label>

        <div className="mt-2 flex justify-end gap-3">
          <Button variant="outline" className="h-11 rounded-lg" onClick={() => onOpenChange(false)} data-testid="photo-manager-cancel-btn">
            Cancel
          </Button>
          <Button
            className="h-11 rounded-lg font-bold hover:brightness-95"
            onClick={save}
            disabled={saving}
            data-testid="photo-manager-save-btn"
          >
            {saving ? "Saving…" : "Save order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoManagerDialog;
